var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useUXPContext } from '@core/context/useUXPContext';
import { useRouterContext, useSearchParams } from '@hooks/useRouteHandlers';
import { getInjectionsForObjectType, onInjectionRegistryChange } from './tabInjectionRegistry';
import { getHardcodedTabs, onHardcodedTabsChange } from './hardcodedTabsRegistry';
import { saveObjectTab, deleteObjectTab, getAllUserGroups } from '@core/services/services';
import { SlideInForm } from '@widget/forms/DataEntryForm/SlideInForm';
import { DataEntrySection } from '@widget/forms/DataEntryForm/DataEntrySection';
import { DataEntryField } from '@widget/forms/DataEntryForm/DataEntryField';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import AppRoleInput from '@widget/forms/inputs/AppRoleInput';
import Checkbox from '@widget/forms/inputs/Checkbox';
import MultiSelect from '@widget/forms/inputs/MultiSelect';
import IconPicker from '@widget/forms/inputs/IconPicker/IconPicker';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { getFontAwesomeIcon, generateUUID, lookupComponent } from '@utils';
import Chip from '@widget/other/Chip';
import useAlert from '@notifications/Alerts/useAlerts';
import useToast from '@notifications/Toast/useToast';
import ComponentRenderer from '@helper/ComponentRenderer/ComponentRenderer';
const TabsEditor = ({ objectType, previewMode = 'preview' }) => {
    var _a, _b;
    const context = useUXPContext();
    const alerts = useAlert();
    const toast = useToast();
    const { navigate, generateURL } = useRouterContext();
    const searchParams = useSearchParams();
    const selectedPreviewTabId = (_a = searchParams.pe_tab) !== null && _a !== void 0 ? _a : null;
    const setSelectedPreviewTabId = (id) => {
        if (id)
            navigate(generateURL({ searchParams: { pe_tab: id } }), { replace: true });
        else
            navigate(generateURL({ removeParams: ['pe_tab'] }), { replace: true });
    };
    const [injections, setInjections] = useState([]);
    const [hardcodedTabs, setHardcodedTabs] = useState([]);
    const [editingTab, setEditingTab] = useState(null);
    const [configuringComponent, setConfiguringComponent] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [allUserGroups, setAllUserGroups] = useState([]);
    const activeTabs = useMemo(() => {
        var _a;
        const tabs = ((_a = context === null || context === void 0 ? void 0 : context.objectTabs) === null || _a === void 0 ? void 0 : _a[objectType.toLowerCase()]) || [];
        return [...tabs].sort((a, b) => a.priority - b.priority);
    }, [context === null || context === void 0 ? void 0 : context.objectTabs, objectType]);
    useEffect(() => {
        const update = () => setInjections(getInjectionsForObjectType(objectType));
        update();
        return onInjectionRegistryChange(update);
    }, [objectType]);
    useEffect(() => {
        const update = () => setHardcodedTabs(getHardcodedTabs(objectType));
        update();
        return onHardcodedTabsChange(update);
    }, [objectType]);
    useEffect(() => {
        getAllUserGroups(context).then(groups => setAllUserGroups(groups || []));
    }, []);
    const mergedTabs = useMemo(() => {
        return injections.map(injection => {
            var _a;
            const dbRecord = activeTabs.find(t => { var _a; return ((_a = t.contributionId) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === injection.id.toLowerCase(); });
            const isEnabled = !dbRecord || dbRecord.status !== 'Disabled';
            const isOverridden = !!dbRecord && dbRecord.status !== 'Disabled' && (dbRecord.name !== injection.name ||
                (dbRecord.icon || '') !== (injection.icon || '') ||
                (dbRecord.appRoles || '') !== (injection.appRoles || []).join(',') ||
                ((_a = dbRecord.components) === null || _a === void 0 ? void 0 : _a.some(c => Object.keys(c.configuredProps || {}).length > 0)));
            return { injection, dbRecord, isEnabled, isOverridden };
        });
    }, [injections, activeTabs]);
    const allGutterTabs = useMemo(() => [
        ...hardcodedTabs.map(t => ({
            id: t.id,
            icon: t.icon || 'fas puzzle-piece',
            label: t.label || t.id,
            isHardcoded: true,
        })),
        ...mergedTabs.filter(t => t.isEnabled).map(t => {
            var _a, _b;
            return ({
                id: t.injection.id,
                icon: ((_a = t.dbRecord) === null || _a === void 0 ? void 0 : _a.icon) || t.injection.icon || 'fas puzzle-piece',
                label: ((_b = t.dbRecord) === null || _b === void 0 ? void 0 : _b.name) || t.injection.name,
                isHardcoded: false,
            });
        }),
    ], [hardcodedTabs, mergedTabs]);
    const selectedMergedTab = useMemo(() => mergedTabs.find(t => t.injection.id === selectedPreviewTabId) || null, [mergedTabs, selectedPreviewTabId]);
    const isHardcodedSelected = hardcodedTabs.some(t => t.id === selectedPreviewTabId);
    const selectedGutterTab = useMemo(() => allGutterTabs.find(t => t.id === selectedPreviewTabId) || null, [allGutterTabs, selectedPreviewTabId]);
    const selectedTabComponents = useMemo(() => {
        var _a;
        if (!selectedMergedTab)
            return [];
        const dbComponents = (_a = selectedMergedTab.dbRecord) === null || _a === void 0 ? void 0 : _a.components;
        const comps = (Array.isArray(dbComponents) && dbComponents.length > 0)
            ? dbComponents
            : selectedMergedTab.injection.components.map(c => ({
                componentId: c.componentId,
                configuredProps: {},
                priority: c.priority,
            }));
        return [...comps].sort((a, b) => a.priority - b.priority);
    }, [selectedMergedTab]);
    const configuringSchema = useMemo(() => {
        var _a;
        if (!configuringComponent)
            return [];
        const def = lookupComponent(configuringComponent.comp.componentId);
        const props = ((_a = def === null || def === void 0 ? void 0 : def.configs) === null || _a === void 0 ? void 0 : _a.props) || [];
        return props.filter((p) => 'name' in p);
    }, [configuringComponent]);
    const handleToggle = useCallback((merged, enabled) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const { injection, dbRecord } = merged;
        setBusyId(injection.id);
        try {
            if (!enabled) {
                const tabId = (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.tabId) || generateUUID();
                const { error } = yield saveObjectTab(context, {
                    tabId,
                    objectType: objectType.toLowerCase(),
                    contributionId: injection.id,
                    name: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.name) || injection.name,
                    icon: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.icon) || injection.icon || '',
                    priority: (_a = dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.priority) !== null && _a !== void 0 ? _a : (activeTabs.length + 1),
                    appRoles: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.appRoles) || (injection.appRoles || []).join(','),
                    userGroups: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.userGroups) || (injection.userGroups || []).join(','),
                    status: 'Disabled',
                    components: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.components) || injection.components.map(c => ({
                        componentId: c.componentId,
                        configuredProps: {},
                        priority: c.priority,
                    })),
                });
                if (error) {
                    alerts.error(String(error));
                    return;
                }
                toast.success(`"${injection.name}" disabled`);
                if (selectedPreviewTabId === injection.id)
                    setSelectedPreviewTabId(null);
            }
            else {
                if (dbRecord) {
                    const hasCustomisation = dbRecord.name !== injection.name ||
                        (dbRecord.icon || '') !== (injection.icon || '') ||
                        (dbRecord.appRoles || '') !== (injection.appRoles || []).join(',') ||
                        ((_b = dbRecord.components) === null || _b === void 0 ? void 0 : _b.some(c => Object.keys(c.configuredProps || {}).length > 0));
                    if (hasCustomisation) {
                        const { error } = yield saveObjectTab(context, Object.assign(Object.assign({}, dbRecord), { status: 'Active' }));
                        if (error) {
                            alerts.error(String(error));
                            return;
                        }
                    }
                    else {
                        const { error } = yield deleteObjectTab(context, dbRecord.tabId);
                        if (error) {
                            alerts.error(String(error));
                            return;
                        }
                    }
                }
                toast.success(`"${injection.name}" enabled`);
            }
            yield context.refreshObjectTabs();
        }
        catch (_c) {
            alerts.error('Failed to update tab status');
        }
        finally {
            setBusyId(null);
        }
    }), [context, objectType, activeTabs, selectedPreviewTabId]);
    const handleSave = useCallback((formData) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!editingTab)
            return;
        const { injection, dbRecord } = editingTab;
        const tabId = (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.tabId) || generateUUID();
        const { error } = yield saveObjectTab(context, {
            tabId,
            objectType: objectType.toLowerCase(),
            contributionId: injection.id,
            name: formData.name || injection.name,
            icon: formData.icon || '',
            priority: (_a = dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.priority) !== null && _a !== void 0 ? _a : (activeTabs.length + 1),
            appRoles: formData.appRoles || '',
            userGroups: formData.userGroups || '',
            status: 'Active',
            components: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.components) || injection.components.map(c => ({
                componentId: c.componentId,
                configuredProps: {},
                priority: c.priority,
            })),
        });
        if (error)
            throw new Error(String(error));
        yield context.refreshObjectTabs();
        setEditingTab(null);
        toast.success('Tab saved');
    }), [context, objectType, activeTabs, editingTab]);
    const handleReset = useCallback((merged) => __awaiter(void 0, void 0, void 0, function* () {
        if (!merged.dbRecord)
            return;
        const confirmed = yield alerts.confirm({
            title: 'Reset to defaults?',
            content: `"${merged.injection.name}" will revert to its default settings.`,
        });
        if (!confirmed)
            return;
        setBusyId(merged.injection.id);
        try {
            const { error } = yield deleteObjectTab(context, merged.dbRecord.tabId);
            if (error) {
                alerts.error(String(error));
                return;
            }
            yield context.refreshObjectTabs();
            if ((editingTab === null || editingTab === void 0 ? void 0 : editingTab.injection.id) === merged.injection.id)
                setEditingTab(null);
            toast.success('Tab reset to defaults');
        }
        catch (_a) {
            alerts.error('Failed to reset tab');
        }
        finally {
            setBusyId(null);
        }
    }), [context, editingTab]);
    const handleComponentConfigSave = useCallback((formData) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!configuringComponent)
            return;
        const { merged, comp } = configuringComponent;
        const { injection, dbRecord } = merged;
        const tabId = (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.tabId) || generateUUID();
        const baseComponents = (Array.isArray(dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.components) && (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.components.length) > 0)
            ? dbRecord.components
            : injection.components.map(c => ({
                componentId: c.componentId,
                configuredProps: {},
                priority: c.priority,
            }));
        const updatedComponents = baseComponents.map(c => c.componentId === comp.componentId ? Object.assign(Object.assign({}, c), { configuredProps: formData }) : c);
        const { error } = yield saveObjectTab(context, {
            tabId,
            objectType: objectType.toLowerCase(),
            contributionId: injection.id,
            name: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.name) || injection.name,
            icon: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.icon) || injection.icon || '',
            priority: (_a = dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.priority) !== null && _a !== void 0 ? _a : (activeTabs.length + 1),
            appRoles: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.appRoles) || (injection.appRoles || []).join(','),
            userGroups: (dbRecord === null || dbRecord === void 0 ? void 0 : dbRecord.userGroups) || (injection.userGroups || []).join(','),
            status: 'Active',
            components: updatedComponents,
        });
        if (error)
            throw new Error(String(error));
        yield context.refreshObjectTabs();
        setConfiguringComponent(null);
        toast.success('Component configured');
    }), [context, objectType, activeTabs, configuringComponent]);
    const editItem = useMemo(() => {
        var _a, _b, _c, _d;
        if (!editingTab)
            return null;
        return {
            name: ((_a = editingTab.dbRecord) === null || _a === void 0 ? void 0 : _a.name) || editingTab.injection.name,
            icon: ((_b = editingTab.dbRecord) === null || _b === void 0 ? void 0 : _b.icon) || editingTab.injection.icon || '',
            appRoles: ((_c = editingTab.dbRecord) === null || _c === void 0 ? void 0 : _c.appRoles) || (editingTab.injection.appRoles || []).join(','),
            userGroups: ((_d = editingTab.dbRecord) === null || _d === void 0 ? void 0 : _d.userGroups) || (editingTab.injection.userGroups || []).join(','),
        };
    }, [editingTab]);
    if (mergedTabs.length === 0) {
        return (_jsx("div", { className: "uxpcore_tabseditor", children: _jsxs("div", { className: "uxpcore_tabseditor__empty", children: ["No injected tabs registered for ", _jsx("strong", { children: objectType }), ". Load an app that injects into this object type."] }) }));
    }
    return (_jsxs("div", { className: "uxpcore_tabseditor", children: [_jsxs("div", { className: "uxpcore_tabseditor__body", children: [_jsxs("div", { className: "uxpcore_tabseditor__list", children: [_jsx("div", { className: "uxpcore_tabseditor__section_header", children: context.$L('uxp-core.page-editor.injected-tabs') }), mergedTabs.map(item => {
                                var _a, _b;
                                return (_jsxs("div", { className: classNames('uxpcore_tabseditor__tab_row', {
                                        'uxpcore_tabseditor__tab_row--selected': selectedPreviewTabId === item.injection.id,
                                        'uxpcore_tabseditor__tab_row--disabled': !item.isEnabled,
                                    }), onClick: () => setSelectedPreviewTabId(item.injection.id), children: [_jsxs("div", { className: "uxpcore_tabseditor__tab_row_info", children: [_jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(((_a = item.dbRecord) === null || _a === void 0 ? void 0 : _a.icon) || item.injection.icon || 'fas puzzle-piece') }), _jsx("span", { className: "uxpcore_tabseditor__tab_row_name", children: ((_b = item.dbRecord) === null || _b === void 0 ? void 0 : _b.name) || item.injection.name }), item.isOverridden && (_jsx(Chip, { label: "Modified", size: "small", backgroundColor: context.theme.modifiedStatusBGColor, textColor: context.theme.modifiedStatusTextColor }))] }), _jsxs("div", { className: "uxpcore_tabseditor__tab_row_actions", children: [_jsx(Checkbox, { type: "switch-box", checked: item.isEnabled, onChange: checked => handleToggle(item, checked), disabled: busyId === item.injection.id }), _jsx(ButtonComponent, { leftIcon: "fas pencil", iconOnly: true, variant: "secondary", onClick: e => { e.stopPropagation(); setEditingTab(item); }, disabled: busyId === item.injection.id || !item.isEnabled, title: "Configure tab" }), item.isOverridden && (_jsx(ButtonComponent, { leftIcon: "fas undo", iconOnly: true, variant: "danger", onClick: e => { e.stopPropagation(); handleReset(item); }, disabled: busyId === item.injection.id, title: "Reset to defaults" }))] })] }, item.injection.id));
                            })] }), _jsxs("div", { className: "uxpcore_tabseditor__preview_panel", children: [_jsx("div", { className: "uxpcore_tabseditor__section_header", children: context.$L('uxp-core.page-editor.preview') }), _jsxs("div", { className: "uxpcore_tabseditor__preview_panel__inner", children: [_jsx("div", { className: "uxpcore_obsdp__additional_details_container uxpcore_obsdp__additional_details_container--open", children: !selectedPreviewTabId ? (_jsx("div", { className: "uxpcore_obsdp__additional_details_notfound", children: "Select a tab from the strip on the right to preview its content" })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_obsdp__additional_details_header", children: [_jsx("div", { className: "uxpcore_obsdp__additional_details_header-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon((selectedGutterTab === null || selectedGutterTab === void 0 ? void 0 : selectedGutterTab.icon) || 'fas puzzle-piece') }) }), _jsx("div", { className: "uxpcore_obsdp__additional_details_header-label", children: selectedGutterTab === null || selectedGutterTab === void 0 ? void 0 : selectedGutterTab.label })] }), _jsx("div", { className: "uxpcore_obsdp__additional_details_body", children: isHardcodedSelected ? (_jsx("div", { className: "uxpcore_obsdp__additional_details_notfound", children: "Default tab \u2014 content is not configurable here." })) : selectedMergedTab ? (_jsx("div", { className: "uxpcore_injectedtab__content", children: selectedTabComponents.map(comp => {
                                                            const parts = comp.componentId.split('/');
                                                            const moduleId = parts[0];
                                                            const componentId = parts.length >= 3 ? parts[2] : parts[1];
                                                            const isConfigured = Object.keys(comp.configuredProps || {}).length > 0;
                                                            return (_jsx(ComponentRenderer, { moduleId: moduleId, componentId: componentId, configuredProps: comp.configuredProps, hasConfigured: previewMode === 'preview' ? isConfigured : false, additionalProps: { objectKey: null, objectType }, onConfigureRequest: () => setConfiguringComponent({ merged: selectedMergedTab, comp }), configNeededMessage: "Configure this widget's settings", forceConfigView: previewMode !== 'preview' }, comp.componentId));
                                                        }) })) : null })] })) }), _jsx("div", { className: "uxpcore_obsdp__gutter_container", children: _jsx("div", { className: "uxpcore_obsdp__additional_details_tabs", children: allGutterTabs.map(t => (_jsx("div", { className: classNames('uxpcore_obsdp__additional_details_tab', {
                                                    'uxpcore_obsdp__additional_details_tab--active': selectedPreviewTabId === t.id,
                                                    'uxpcore_tabseditor__preview_tab--hardcoded': t.isHardcoded,
                                                }), title: t.label, onClick: () => setSelectedPreviewTabId(selectedPreviewTabId === t.id ? null : t.id), children: _jsx("div", { className: "uxpcore_obsdp__additional_details_tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(t.icon) }) }) }, t.id))) }) })] })] })] }), _jsx(SlideInForm, { formType: "data-entry", title: editingTab ? `Configure: ${((_b = editingTab.dbRecord) === null || _b === void 0 ? void 0 : _b.name) || editingTab.injection.name}` : '', isOpen: !!editingTab, item: editItem, onSave: handleSave, onCancel: () => setEditingTab(null), mode: "modal", hideInstructions: true, children: _jsxs(DataEntrySection, { children: [_jsx(DataEntryField, { field: "name", title: "Name", type: "text", placeholder: editingTab === null || editingTab === void 0 ? void 0 : editingTab.injection.name }), _jsx(DataEntryField, { field: "icon", title: "Icon", children: (data, onChange) => (_jsx(IconPicker, { value: data.icon, onChange: onChange, onClear: () => onChange('') })) }), _jsx(DataEntryField, { field: "appRoles", title: "App Roles", children: (data, onChange) => (_jsx(AppRoleInput, { value: data.appRoles, onChange: onChange })) }), _jsx(DataEntryField, { field: "userGroups", title: "User Groups", children: (data, onChange) => (_jsx(MultiSelect, { options: allUserGroups, labelField: "UserGroupID", valueField: "UserGroupID", selected: (data.userGroups || '').split(',').filter(Boolean), onChange: (vals) => onChange(vals.join(',')), placeholder: "All user groups" })) })] }) }), _jsx(SlideInForm, { formType: "data-entry", title: configuringComponent
                    ? `Configure: ${configuringComponent.comp.componentId.split('/').pop()}`
                    : '', isOpen: !!configuringComponent, item: (configuringComponent === null || configuringComponent === void 0 ? void 0 : configuringComponent.comp.configuredProps) || {}, onSave: handleComponentConfigSave, onCancel: () => setConfiguringComponent(null), mode: "modal", hideInstructions: true, children: _jsx(DataEntrySection, { children: configuringSchema.map(p => (_jsx(DataEntryField, { field: p.name, title: p.label || p.name, type: p.type }, p.name))) }) })] }));
};
export default TabsEditor;
